// import "server-only"
import moment from "moment/moment";
import "moment/locale/fr";
moment.locale("fr");
import { ResultSetHeader, RowDataPacket } from "mysql2/promise";
import { logDebug, logError } from "@/utils/helpers";
import mysql from "mysql2/promise";
type ServerResponse = {success : false, message : string, details : string} | {success : true, result : mysql.QueryResult};

export async function execute(query: string, values: unknown = []) {
  try {
    // logDebug(`Sending data: ${values}`);  
    const rawResponse  = await fetch(`${process.env.NEXT_PUBLIC_SOCKET_URL}/execute`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ query, values }),
    });
    // logDebug(`Response status: ${response.status}`);
    const response = await rawResponse.json() as ServerResponse;
    
    // logDebug(`Received data:\n${truncate(JSON.stringify(result, null, 2), 128)}`);
    if (response.success === false) {
      throw new Error(response.details);
    }
    
    return response.result;
  } catch (error) {
    logError("MYSQL ERROR:", (error as Error).message);
    throw error;
  }
}
type DBRow = Record<string, unknown> & { id: number };

export async function SELECT<T extends DBRow>(table: string, options: SelectOptions) {
  // logDebug("SELECT", table, options)
  const { columns = ["*"], conditions, order, limit, isIndexed } = options;
  try {
    const columnNames = columns.join(", ");
    let whereConditions = "";
    let queryValues: any = [];

    if (conditions && conditions.length > 0) {
      const conditionStrings = conditions.map((condition, index: number) => {
        queryValues.push(condition.value);
        return `${index > 0 ? 'AND' : ''} ${condition.column} ${condition.operator} ?`;
      });
      whereConditions = "WHERE " + conditionStrings.join(' ');
    }

    const orderByClause = order ? `ORDER BY ${order} ` : "";
    const limitClause = limit ? `LIMIT ${limit}` : "";
    const query = `SELECT ${columnNames} FROM ${table} ${whereConditions} ${orderByClause} ${limitClause}`;

    // logDebug("select query", query, queryValues);
    const rows = await execute(query, queryValues) as RowDataPacket[] as T[];
    
    if (isIndexed) return indexRows<T>(rows);
    return rows;
  } catch (error) {
    logError("Error reading data:", error);
    throw error;
  }
}

export async function INSERT(table: string, data: Record<string, any>) {
  try {
    moment.locale("fr");
    const columnNames = Object.keys({  ...data,  date: moment().format("YYYY-MM-DD HH:mm:ss"), }).join(", ");

    const placeholders = Object.values({ ...data,date: moment().format("YYYY-MM-DD HH:mm:ss"), }).map(() => "?").join(", ");

    const query = `INSERT INTO ${table} (${columnNames}) VALUES (${placeholders})`;
    const values = Object.values({
      ...data,
      date: moment().format("YYYY-MM-DD HH:mm:ss"),
    });
    const result = (await execute(query, values)) as ResultSetHeader;
    //  console.log("----------------------------------------------")
    //  console.log("insert query", query , values)
    //  console.log("----------------------------------------------")

    const [addedRow] = await execute(`SELECT * from ${table} WHERE id = ${result.insertId}`,values ) as RowDataPacket [];
    // logDebug("Data inserted successfully");
    return addedRow;
  } catch (error) {
    logError("Error adding data:", error);
    throw error;
  }
}

export async function DELETE(table: string, conditions: FilterConditions) {
  try {
    const whereConditions = conditions?.map((condition) => `${condition.column} ${condition.operator} ?`) .join(" AND ");
    const query = `DELETE FROM ${table} WHERE ${whereConditions}`;

    const values = conditions?.map((condition) => condition.value);

    const result = await execute(query, values) as ResultSetHeader;
    // logDebug("Data deleted successfully!");
  } catch (error) {
    logError("Error deleting data:", error);
    throw error;
  }
}

export async function UPDATE(
  table: string,
  data: Record<string, unknown>,
  conditions: FilterConditions
) {
  try {
    const setColumns = Object.keys(data).map((column) => `${column} = ?`).join(", ");

    const whereConditions = conditions ?.map((condition) => `${condition.column} ${condition.operator} ?`) .join(" AND ");

    const values = [ ...Object.values(data), ...conditions!.map((condition) => condition.value),];
    const sanitizedValues = values.map((value) => value !== undefined ? value : null );

    const query = `UPDATE ${table} SET ${setColumns} WHERE ${whereConditions}`;
    // console.log("----------------------------------------------")

    // console.log("UPDATE query", query , values)
    // console.log("----------------------------------------------")

    const result = (await execute(query, sanitizedValues)) as ResultSetHeader;

    // logDebug(result.affectedRows, "rows updated!");
  } catch (error) {
    logError("Error updating data:", error);
    throw error;
  }
}

type SelectOptions = { columns?: string[]; conditions?: FilterConditions | null; order?: string; limit?: number; isIndexed?: boolean;};


// { value: any; column: any; operator: "<" | ">" | "=" | "<>" | "<=" | ">=" | "LIKE" | "IN" | "NOT IN" | "BETWEEN" | "NOT BETWEEN" | "IS NULL" | "IS NOT NULL" };

export type FilterConditions =
  | {
      column: string[] | string;
      operator: "<" | ">" | "=" | "<>" | "<=" | ">=" | "LIKE" | "IN" | "NOT IN" | "BETWEEN" | "NOT BETWEEN" | "IS NULL" | "IS NOT NULL" ;
      value: number | string | null;
    }[]
  ;

function indexRows<T extends DBRow>(rows: T[]) {
  if (!rows) return rows;
  let indexedRows: Record<string, T> = {};
  for (const row of rows) {
    indexedRows[row.id] = row;
  }
  return indexedRows;
}
